import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { cancelBillingEdit, getClientSecretForExistingCustomer, updatePaymentInfo } from 'api/jalapeno';
import useSubscriptionQuery, { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';
import PaymentForm from 'features/account/PaymentForm';
import { Alert } from 'honeydew/Alert';
import Loader, { LoadingIcon } from 'honeydew/Loader';
import useStripeKlarnaPaymentForm from 'hooks/useStripeKlarnaPaymentForm';
import profileStyles from 'pages/account/Profile/styles';
import logError from 'utils/errorUtils';

const mobileHeading = <h2 className="text-body-lg mb-20 font-grotesk-bold lg:hidden">Payment Method</h2>;

export default function Payment({ setPageAlert, stripePromise }) {
    const queryClient = useQueryClient();
    const { data: subscription = {} } = useSubscriptionQuery();

    const { billing = {}, email, id: subscriptionID } = subscription;
    const phone = subscription?.shippingAddr?.phone;
    const { expMonth, expYear, last4, zip } = billing;

    const [isEditing, setIsEditing] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Payment form state
    const [clientSecret, setClientSecret] = useState(null);
    const [paymentSetupSuccess, setPaymentSetupSuccess] = useState(false);
    const [paymentTypeSelected, setPaymentTypeSelected] = useState('');
    const [setupIntentID, setSetupIntentID] = useState(null);
    const [block, setBlock] = useState(false);
    const [blockMessage, setBlockMessage] = useState('');

    const {
        clearKlarnaParams,
        errorMessage: klarnaError,
        loading: klarnaLoading,
    } = useStripeKlarnaPaymentForm({
        setPaymentSetupSuccess,
        setSetupIntentID,
        setPaymentTypeSelected,
    });

    const clearBilling = () => {
        setClientSecret(null);
        setSetupIntentID(null);
        setPaymentSetupSuccess(false);
        setPaymentTypeSelected('');
    };

    const onEditClick = async () => {
        setIsEditing(true);
        try {
            const response = await getClientSecretForExistingCustomer(subscriptionID, {
                email,
                klarnaEnabled: true,
            });
            if (response?.data?.block) {
                setBlock(true);
                setBlockMessage(response.data.block);
                clearBilling();
            } else if (response?.data?.secret) {
                setClientSecret(response.data.secret);
            }
        } catch (err) {
            logError(err);
            console.error(err);
        }
    };

    const onCancelClick = async () => {
        setIsEditing(false);
        if (clientSecret !== null && setupIntentID !== null) {
            clearBilling();
            try {
                await cancelBillingEdit(subscriptionID, { stripeSetupIntentID: setupIntentID });
            } catch (err) {
                logError(err);
                console.error(err);
            }
        }
    };

    // Handle Klarna redirect - set isEditing when processing Klarna payment or if there's an error
    useEffect(() => {
        if (klarnaLoading || klarnaError) {
            setIsEditing(true);
        }
    }, [klarnaLoading, klarnaError]);

    useEffect(() => {
        if (paymentSetupSuccess && setupIntentID !== null) {
            (async () => {
                try {
                    setIsUpdating(true);
                    const response = await updatePaymentInfo(subscriptionID, {
                        stripeSetupIntentID: setupIntentID,
                        paymentMethod: paymentTypeSelected,
                    });
                    setPageAlert(response?.msg === 'Success' ? 'success.payment' : 'error.payment');
                    await queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
                } catch (err) {
                    logError(err);
                    console.error(err);
                } finally {
                    setIsEditing(false);
                    setIsUpdating(false);
                    window.scroll({
                        top: 0,
                        behavior: 'smooth',
                    });
                    clearBilling();
                    clearKlarnaParams();
                }
            })();
        }
    }, [paymentSetupSuccess, setupIntentID, clearKlarnaParams]);

    let editElement;
    if (isUpdating) {
        editElement = (
            <div className="flex gap-10">
                <LoadingIcon />
                <p className="text-body-sm">Updating payment method...</p>
            </div>
        );
    } else if (klarnaError) {
        editElement = <Alert variant="error">{klarnaError}</Alert>;
    } else if (clientSecret === null && setupIntentID === null && !block) {
        editElement = (
            <div className="flex justify-center">
                <Loader />
            </div>
        );
    } else if (block) {
        editElement = <Alert variant="error">{blockMessage}</Alert>;
    } else {
        editElement = (
            <PaymentForm
                email={email}
                clientSecret={clientSecret}
                paymentSetupSuccess={paymentSetupSuccess}
                phone={phone}
                setPaymentSetupSuccess={setPaymentSetupSuccess}
                setPaymentTypeSelected={setPaymentTypeSelected}
                setSetupIntentID={setSetupIntentID}
                stripePromise={stripePromise}
            />
        );
    }

    return isEditing ? (
        <div className={profileStyles.editContainer}>
            {mobileHeading}
            <div>
                {editElement}
                {!isUpdating && (
                    <button type="button" className="link text-body-sm mx-auto mt-15 block" onClick={onCancelClick}>
                        Cancel
                    </button>
                )}
            </div>
        </div>
    ) : (
        <div className={profileStyles.displayContainer}>
            <div className="text-body-md">
                {mobileHeading}
                <p className="text-grey">CREDIT CARD</p>
                <p>
                    Expires on {expMonth}/{expYear}
                </p>
                <p>Billing Zip: {zip}</p>
                <p>Card: **** **** **** {last4}</p>
            </div>
            <button type="button" onClick={onEditClick} className={`${profileStyles.edit} self-start lg:self-center`}>
                Edit
            </button>
        </div>
    );
}
